import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import api, { API } from "../../lib/api";
import { toast } from "sonner";

const REPORTS = [
  { id: "sales", label: "Sales", endpoint: "/admin/reports/sales" },
  { id: "top-products", label: "Top products", endpoint: "/admin/reports/top-products" },
  { id: "low-stock", label: "Low stock", endpoint: "/admin/reports/low-stock" },
  { id: "customer-ltv", label: "Customer LTV", endpoint: "/admin/reports/customer-ltv" },
  { id: "gst-summary", label: "GST summary", endpoint: "/admin/reports/gst-summary" },
];

export default function AdminReports() {
  const [tab, setTab] = useState("sales");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [groupBy, setGroupBy] = useState("day");
  const [threshold, setThreshold] = useState(5);
  const [rows, setRows] = useState([]);
  const [extra, setExtra] = useState(null);

  const current = REPORTS.find((r) => r.id === tab);

  useEffect(() => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (tab === "sales") params.set("group_by", groupBy);
    if (tab === "low-stock") params.set("threshold", String(threshold));
    api.get(`${current.endpoint}?${params.toString()}`).then((r) => {
      setRows(r.data.items || []);
      setExtra(r.data.totals || null);
    });
  }, [tab, dateFrom, dateTo, groupBy, threshold, current.endpoint]);

  const exportCsv = () => {
    const params = new URLSearchParams();
    params.set("export", "csv");
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (tab === "sales") params.set("group_by", groupBy);
    if (tab === "low-stock") params.set("threshold", String(threshold));
    const token = localStorage.getItem("jow_token");
    fetch(`${API}${current.endpoint}?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${tab}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Downloaded");
      });
  };

  const headers = rows.length > 0 ? Object.keys(rows[0]) : [];

  return (
    <div className="space-y-5" data-testid="admin-reports">
      <div>
        <span className="label-overline text-jow-gold">Insights</span>
        <h1 className="font-display text-3xl text-jow-navy mt-1">Reports</h1>
      </div>

      <div className="inline-flex flex-wrap border border-jow-navy/15 bg-white">
        {REPORTS.map((r) => (
          <button
            key={r.id}
            onClick={() => setTab(r.id)}
            className={`px-4 py-2 text-[11px] uppercase tracking-widest ${
              tab === r.id ? "bg-jow-navy text-jow-cream" : "text-jow-navy/60"
            }`}
            data-testid={`rep-tab-${r.id}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-jow-navy/10 p-4 flex flex-wrap gap-3 items-end">
        {(tab === "sales" || tab === "top-products" || tab === "gst-summary") && (
          <>
            <FInput label="From" type="date" value={dateFrom} onChange={setDateFrom} testid="rep-from" />
            <FInput label="To" type="date" value={dateTo} onChange={setDateTo} testid="rep-to" />
          </>
        )}
        {tab === "sales" && (
          <div>
            <label className="label-overline block mb-1">Group by</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="border border-jow-navy/15 px-3 py-2 text-sm bg-white"
              data-testid="rep-groupby"
            >
              <option value="day">Day</option>
              <option value="city">City</option>
              <option value="rikshaw">Rikshaw</option>
            </select>
          </div>
        )}
        {tab === "low-stock" && (
          <FInput
            label="Threshold"
            type="number"
            value={threshold}
            onChange={(v) => setThreshold(Number(v))}
            testid="rep-threshold"
          />
        )}
        <button
          onClick={exportCsv}
          className="btn-secondary-jow text-[11px]"
          data-testid="rep-export"
        >
          <Download size={13} /> Download CSV
        </button>
      </div>

      <div className="bg-white border border-jow-navy/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-jow-navy/5 uppercase tracking-widest text-[10px] text-jow-navy/70">
            <tr>
              {headers.map((h) => (
                <th key={h} className="text-left px-4 py-3 capitalize">
                  {h.replaceAll("_", " ")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-jow-navy/5" data-testid={`rep-row-${i}`}>
                {headers.map((h) => (
                  <td key={h} className="px-4 py-2 text-jow-navy/80">
                    {typeof r[h] === "number"
                      ? h.includes("revenue") || h.includes("ltv") || h === "tax" || h === "subtotal" || h === "total" || h === "discount" || h === "shipping"
                        ? `₹${r[h].toLocaleString("en-IN")}`
                        : r[h]
                      : String(r[h] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={headers.length || 1} className="text-center py-8 text-jow-navy/50">
                  No rows.
                </td>
              </tr>
            )}
          </tbody>
          {extra && (
            <tfoot>
              <tr className="bg-jow-cream/50">
                <td className="px-4 py-2 font-display text-jow-navy">Totals</td>
                {headers.slice(1).map((h) => (
                  <td key={h} className="px-4 py-2 font-display">
                    {typeof extra[h] === "number"
                      ? h === "orders"
                        ? extra[h]
                        : `₹${extra[h].toLocaleString("en-IN")}`
                      : ""}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function FInput({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="label-overline block mb-1">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="border border-jow-navy/15 px-3 py-2 text-sm bg-white"
        data-testid={testid}
      />
    </div>
  );
}
